import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { api, API, tokenStore } from "../../lib/api";
import {
  Download,
  RefreshCw,
  Search,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevRight,
  User,
  FileText,
  Trash2,
  Pencil,
  Plus,
  LogIn,
  LogOut,
  AlertTriangle,
} from "lucide-react";

const PER_PAGE = 50;

const ACTION_META = {
  create: { icon: Plus, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Creación" },
  update: { icon: Pencil, cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Modificación" },
  delete: { icon: Trash2, cls: "bg-red-50 text-red-700 border-red-200", label: "Eliminación" },
  login: { icon: LogIn, cls: "bg-slate-50 text-slate-700 border-slate-200", label: "Inicio de sesión" },
  logout: { icon: LogOut, cls: "bg-slate-50 text-slate-700 border-slate-200", label: "Cierre de sesión" },
  failed_login: { icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Login fallido" },
  retry: { icon: RefreshCw, cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Reintento" },
};

const RESOURCE_LABEL = {
  auth: "Autenticación",
  team_member: "Equipo",
  user: "Usuario",
  settings: "Ajustes",
  page: "Página",
  pages: "Página",
  news: "Noticia",
  events: "Evento",
  members: "Socio",
  hero_slides: "Hero",
  homepage_logos: "Logo portada",
  newsletter: "Newsletter",
  "newsletter.subscribers": "Suscriptor",
  submissions: "Mensaje",
  upload: "Archivo",
};

const ACTION_OPTS = ["create", "update", "delete", "login", "logout", "failed_login", "retry"];
const RESOURCE_OPTS = [
  "auth", "team_member", "user", "settings", "pages",
  "news", "events", "members", "hero_slides", "homepage_logos",
  "newsletter", "newsletter.subscribers", "submissions",
];

export default function AuditLogsAdmin() {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({ q: "", action: "", resource_type: "", user_email: "", date_from: "", date_to: "" });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, per_page: PER_PAGE };
      for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
      const { data } = await api.get("/admin/audit-logs", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al cargar los registros");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const applyFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
    fetch(`${API}/admin/audit-logs/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div data-testid="cms-audit-logs">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040] flex items-center gap-2">
            <Shield className="w-5 h-5" /> Registro de auditoría
          </h1>
          <p className="text-sm text-gray-600">
            Trazabilidad de todas las acciones administrativas. Requerido para el cumplimiento RGPD.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="audit-refresh"
            className="px-3 py-2 text-sm border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-4 h-4" /> Refrescar
          </button>
          <button
            onClick={exportCsv}
            data-testid="audit-export"
            className="bg-[#0F2040] text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-[#1A2F5C]"
          >
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 p-4 mb-4 grid gap-3 md:grid-cols-5" data-testid="audit-filters">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar por email, recurso o ID…"
            value={filters.q}
            onChange={(e) => applyFilters({ q: e.target.value })}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm bg-white"
            data-testid="audit-search"
          />
        </div>
        <select
          value={filters.action}
          onChange={(e) => applyFilters({ action: e.target.value })}
          className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm bg-white"
          data-testid="audit-action-filter"
        >
          <option value="">Todas las acciones</option>
          {ACTION_OPTS.map((a) => (
            <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
          ))}
        </select>
        <select
          value={filters.resource_type}
          onChange={(e) => applyFilters({ resource_type: e.target.value })}
          className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm bg-white"
          data-testid="audit-resource-filter"
        >
          <option value="">Todos los recursos</option>
          {RESOURCE_OPTS.map((r) => (
            <option key={r} value={r}>{RESOURCE_LABEL[r] || r}</option>
          ))}
        </select>
        <div className="grid grid-cols-2 gap-2 md:col-span-1">
          <input
            type="date"
            value={filters.date_from.slice(0, 10)}
            onChange={(e) => applyFilters({ date_from: e.target.value ? `${e.target.value}T00:00:00Z` : "" })}
            className="w-full px-2 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm bg-white"
            data-testid="audit-date-from"
            title="Desde"
          />
          <input
            type="date"
            value={filters.date_to.slice(0, 10)}
            onChange={(e) => applyFilters({ date_to: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
            className="w-full px-2 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm bg-white"
            data-testid="audit-date-to"
            title="Hasta"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-700" data-testid="audit-error">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-left px-4 py-3 w-6"></th>
              <th className="text-left px-4 py-3 w-40">Fecha</th>
              <th className="text-left px-4 py-3">Usuario</th>
              <th className="text-left px-4 py-3">Acción</th>
              <th className="text-left px-4 py-3">Recurso</th>
              <th className="text-left px-4 py-3">ID</th>
              <th className="text-left px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400" data-testid="audit-empty">Sin registros que coincidan con los filtros.</td></tr>
            ) : (
              items.map((r) => {
                const meta = ACTION_META[r.action] || { icon: FileText, cls: "bg-gray-50 text-gray-700 border-gray-200", label: r.action };
                const Icon = meta.icon;
                const isOpen = expanded[r.id];
                const hasDiff = r.changes && (r.changes.before || r.changes.after || r.changes.extra);
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => hasDiff && setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}
                      data-testid={`audit-row-${r.id}`}
                    >
                      <td className="px-4 py-3 text-gray-400">
                        {hasDiff ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevRight className="w-4 h-4" />) : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(r.timestamp)}</td>
                      <td className="px-4 py-3 text-[#0F2040]">
                        <span className="inline-flex items-center gap-1.5">
                          <User className="w-3 h-3 text-gray-400" />
                          {r.user_email || <span className="text-gray-400 italic">anónimo</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 border inline-flex items-center gap-1 ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{RESOURCE_LABEL[r.resource_type] || r.resource_type || "—"}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">{r.resource_id || "—"}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">{r.ip || "—"}</td>
                    </tr>
                    {isOpen && hasDiff && (
                      <tr key={`${r.id}-diff`} className="bg-gray-50/60" data-testid={`audit-diff-${r.id}`}>
                        <td></td>
                        <td colSpan={6} className="px-4 py-4">
                          <DiffView changes={r.changes} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm" data-testid="audit-pagination">
        <p className="text-gray-500">
          {total === 0
            ? "0 registros"
            : `Página ${page} de ${totalPages} · ${total} registros`}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="audit-prev-page"
            className="px-3 py-2 border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            data-testid="audit-next-page"
            className="px-3 py-2 border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DiffView({ changes }) {
  const before = changes?.before || {};
  const after = changes?.after || {};
  const extra = changes?.extra;
  const keys = useMemo(() => Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort(), [before, after]);
  return (
    <div className="text-xs">
      {keys.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 bg-white">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 w-40 font-medium">Campo</th>
                <th className="text-left px-3 py-2 font-medium">Antes</th>
                <th className="text-left px-3 py-2 font-medium">Después</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k} className="border-t border-gray-100 align-top">
                  <td className="px-3 py-2 font-mono text-[#0F2040]">{k}</td>
                  <td className="px-3 py-2 text-red-700 whitespace-pre-wrap break-all">{fmt(before[k])}</td>
                  <td className="px-3 py-2 text-emerald-700 whitespace-pre-wrap break-all">{fmt(after[k])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {extra && (
        <p className="mt-2 text-gray-500">
          <span className="uppercase tracking-wider text-[10px]">Extra: </span>
          <code>{JSON.stringify(extra)}</code>
        </p>
      )}
    </div>
  );
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  const s = String(v);
  return s.length > 400 ? s.slice(0, 400) + "…" : s;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) +
      " " +
      d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    );
  } catch {
    return iso;
  }
}
